import { useCallback, useEffect, useRef, useState } from 'react';

import { getPatientGoalsForVisit } from '../services/api';
import type { TaskListResponse } from '../services/api/types.gen';

/**
 * Tracks the number of active goals (not met, not unattainable) for the selected
 * visit — the value shown on the Goals tab badge. Exposes a `refreshActiveGoalCount`
 * callback so a goal mutation elsewhere can update the badge immediately instead of
 * waiting for a visit change or tab switch.
 *
 * The count is visit-scoped, so it resets to 0 when no visit is selected or the
 * selected task isn't a visit. A monotonic request id discards out-of-order
 * responses — including one still in flight when the visit is deselected — so a
 * slower earlier fetch can never overwrite a newer count or a reset.
 */
export function useActiveGoalCount(
  selectedVisitId: string | null,
  tasks: TaskListResponse[],
  // Re-run trigger: the badge should recompute when the user switches tabs.
  activeTab: string
): { activeGoalCount: number; refreshActiveGoalCount: () => Promise<void> } {
  const [activeGoalCount, setActiveGoalCount] = useState(0);
  const requestRef = useRef(0);

  const refreshActiveGoalCount = useCallback(async () => {
    // Bump the request id first — before any early return — so deselecting the
    // visit (or switching to a non-visit task) supersedes an in-flight fetch
    // instead of letting its late response clobber the reset.
    const requestId = ++requestRef.current;
    if (!selectedVisitId) {
      setActiveGoalCount(0);
      return;
    }
    const selectedTask = tasks.find(t => t.id === selectedVisitId);
    if (selectedTask?.task_type !== 'visit') {
      setActiveGoalCount(0);
      return;
    }
    try {
      // throwOnError so HTTP failures reach the catch below; the client's
      // default (false) resolves with an error result whose `data` is
      // undefined, which would silently reset the badge to 0.
      const goalsRes = await getPatientGoalsForVisit({
        path: { visit_id: selectedVisitId },
        throwOnError: true,
      });
      // A newer refresh superseded this one while it was in flight; drop it.
      if (requestId !== requestRef.current) return;
      const goals = goalsRes?.data ?? [];
      setActiveGoalCount(
        goals.filter(goal => !goal.is_met && !goal.is_unattainable).length
      );
    } catch (err) {
      // A transient fetch failure must not blank the badge (it's rendered only
      // when count > 0) and read as "no goals". Keep the last-known count and
      // log; ignore a superseded request's failure entirely. The legitimate
      // reset-to-0 cases (no visit / non-visit task) are the synchronous early
      // returns above, not this path.
      if (requestId === requestRef.current) {
        console.error('Failed to refresh active goal count', err);
      }
    }
  }, [selectedVisitId, tasks]);

  // Recompute on visit change, task refresh, and tab switch.
  useEffect(() => {
    void refreshActiveGoalCount();
  }, [refreshActiveGoalCount, activeTab]);

  return { activeGoalCount, refreshActiveGoalCount };
}
